import React, { createContext, useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { useAlert } from '../../alert/containers/AlertProvider'
import {
  getChallengeParticipationTotalApi,
  getChallengeParticipationTopWilayaApi,
  getChallengeParticipationTopDairaApi,
  getChallengeParticipationPerWilayaApi,
  getChallengeParticipationPerDairaApi,
  getChallengeParticipationPerStatusApi
} from '../api'
import { getWilayaLabel, getWilayaArabicLabel } from '../../../utils/wilayasAndCommunes'
import { getDictionnaryLabelById, getDictionnaryArabicLabelById } from '../../../utils/dictionnary'
import 'moment/locale/fr'
import { useTranslation } from 'react-i18next'
import { getApiDairaName } from '../../Intervenant/api'

moment.locale('fr')
export const ChallengeParticipationsDashboardContext = createContext()

export const initialChallengeParticipationFilter = {
  wilayas: [],
  communes: [],
  dairaId: [],
  statuses: [],
}

const initialDate = {
  // 29 days + today in order to have 30 days
  startDate: moment().subtract(29, 'd'),
  endDate: moment(),
}

const formatChartData = (chartData) =>
  chartData.reduce(
    (acc, curr) => {
      return {
        labels: [...acc.labels, curr.label],
        data: [...acc.data, curr.count],
        ids: [...acc.ids, curr.id],
      }
    },
    { labels: [], data: [], ids: [] },
  )

export const ChallengeParticipationsDashboardProvider = ({ children }) => {
  const { showAlert } = useAlert()
  const [isLoading, setIsLoading] = useState(false)
  const { t, i18n } = useTranslation()

  const [langDir, setlangDir] = useState(document.getElementsByTagName('html')[0].getAttribute('dir'))
  const [date, setDate] = useState(initialDate)
  const [challengeParticipationFilter, setChallengeParticipationFilter] = useState(initialChallengeParticipationFilter)

  const [challengeParticipationStat, setchallengeParticipationStat] = useState({
    numberOfChallengeParticipations: 0,
    challengeParticipationNumberPerDay: 0,
    topWilayaStat: { labels: [], data: [] },
    topDairaStat: { labels: [], data: [] },
    wilayaStat: { labels: [], data: [], ids: [] },
    statusStat: { labels: [], data: [] },
  })
  
  const statusesDictionnary = [
    { id: 1, label: t('complaintsTable.nouvelle') },
    { id: 2, label: t('challengeParticipationsTable.accepted') },
    { id: 3, label: t('complaintsTable.canceled') },
  ]

  const getChallengeParticipationStat = async () => {
    try {
      setIsLoading(true)

      const { wilayas, communes, dairaId, statuses } = challengeParticipationFilter
      
      let numberOfDays = date.endDate.diff(date.startDate, 'days')
      numberOfDays = numberOfDays > 0 ? numberOfDays : 10
      
      const requestFilter = {
        communeIds: communes.map(({ id }) => id),
        dairaIds: dairaId.map(({ id }) => id),
        wilayaIds: wilayas.map(({ id }) => id),
        ...(date.startDate !== null && {
          startDate: moment(
            `${date.startDate.format('YYYY-MM-DD')} 00:00:00`,
          ).format(),
        }),
        statuses,
        ...(date.endDate !== null && {
          endDate: moment(
            `${date.endDate.format('YYYY-MM-DD')} 23:59:59`,
          ).format(),
        }),
      }

      const requestsData = await Promise.all([
        getChallengeParticipationTotalApi({
          filter: requestFilter,
        }),
        getChallengeParticipationTopWilayaApi({
          filter: requestFilter,
        }),
        getChallengeParticipationTopDairaApi({
          filter: requestFilter,
        }),
        getChallengeParticipationPerWilayaApi({
          filter: requestFilter,
        }),
        getChallengeParticipationPerStatusApi({
          filter: requestFilter,
        })
      ])

      const [
        numberOfChallengeParticipations,
        topWilayaStat,
        topDairaStat,
        wilayaStat,
        statusStat
      ] = requestsData.map(({ data }) => data)

      if(document.getElementsByTagName('html')[0].getAttribute('dir') === "ltr") {
        setlangDir("ltr")
        setchallengeParticipationStat({
          numberOfChallengeParticipations,
          challengeParticipationNumberPerDay: numberOfChallengeParticipations / numberOfDays,
          topWilayaStat: {
            label: getWilayaLabel(topWilayaStat.key) === 'N/A'
            ? 'N/A'
            : `${getWilayaLabel(topWilayaStat.key)}`,
            count: topWilayaStat.count
          },
          topDairaStat: {
            label: topDairaStat === ''
            ? 'N/A'
            : `${await getDairaLabel(topDairaStat.key)}`,
            count: topDairaStat.count
          },
          wilayaStat: formatChartData(
            wilayaStat.map(({ key, count }) => ({
              label: getWilayaLabel(key) === 'N/A'
              ? 'N/A'
              : `${getWilayaLabel(key)}`,
              count,
              id: key
            }))
          ),
          statusStat: formatChartData(
            statusStat.map(({ key, count }) => ({
              label: getDictionnaryLabelById({
                id: key,
                dictionnary: statusesDictionnary,
              }),
              count,
            })),
          )
        })
      }
      else {
        setlangDir("rtl")
        setchallengeParticipationStat({
          numberOfChallengeParticipations,
          challengeParticipationNumberPerDay: numberOfChallengeParticipations / numberOfDays,
          topWilayaStat: {
            label: getWilayaArabicLabel(topWilayaStat.key) === 'N/A'
            ? 'N/A'
            : `${getWilayaArabicLabel(topWilayaStat.key)}`,
            count: topWilayaStat.count
          },
          topDairaStat: {
            label: topDairaStat === ''
            ? 'N/A'
            : `${await getDairaArabicLabel(topDairaStat.key)}`,
            count: topDairaStat.count
          },
          wilayaStat: formatChartData(
            wilayaStat.map(({ key, count }) => ({
              label: getWilayaArabicLabel(key) === 'N/A'
              ? 'N/A'
              : `${getWilayaArabicLabel(key)}`,
              count,
              id: key
            }))
          ),
          statusStat: formatChartData(
            statusStat.map(({ key, count }) => ({
              label: getDictionnaryLabelById({
                id: key,
                dictionnary: statusesDictionnary,
              }),
              count,
            })),
          )
        })
      }
    } 
    catch (e) {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } 
    finally {
      setIsLoading(false)
    }
  }

  const init = () => {
    setChallengeParticipationFilter({ ...initialChallengeParticipationFilter })
    setDate({ ...initialDate })
  }

  const getDairaStat = async (wilayaId) => {
    try {
      const { communes, dairaId, wilayas, statuses } = challengeParticipationFilter

      let requestFilter = {
        communeIds: communes.map(({ id }) => id),
        dairaIds: dairaId.map(({ id }) => id),
        wilayaIds: wilayaId === undefined ? wilayas.map(({ id }) => id) : [wilayaId],
        ...(date.startDate !== null && {
          startDate: moment(
            `${date.startDate.format('YYYY-MM-DD')} 00:00:00`,
          ).format(),
        }),
        statuses,
        ...(date.endDate !== null && {
          endDate: moment(
            `${date.endDate.format('YYYY-MM-DD')} 23:59:59`,
          ).format(),
        }),
      }

      setIsLoading(true)
      const { data } = await getChallengeParticipationPerDairaApi({filter: requestFilter})

      let dairaIds = data.map((obj) => {
        return obj.key
      })
  
      let dairaNames = await getApiDairaName(dairaIds)

      let dairaStat = formatChartData(
        i18n.language === "fr" ?
        data.map(({ key, count }) => ({
          label: dairaNames.data.find(daira => daira.id === key).name === 'N/A'
          ? 'N/A'
          : `${dairaNames.data.find(daira => daira.id === key).name}`,
          count,
          id: key
        }))
        :
        data.map(({ key, count }) => ({
          label: dairaNames.data.find(daira => daira.id === key).name_ar === 'N/A'
          ? 'N/A'
          : `${dairaNames.data.find(daira => daira.id === key).name_ar}`,
          count,
          id: key
        }))
      )
      setchallengeParticipationStat({...challengeParticipationStat, dairaStat: dairaStat})
    }
    catch (e) {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } 
    finally {
      setIsLoading(false)
    }
  }

  const getDairaLabel = async (dairaId) => {
    let dairaLabel = await getApiDairaName([dairaId])
    if (dairaLabel.data.length > 0) return dairaLabel.data[0].name
    else return 'N/A'
  }

  const getDairaArabicLabel = async (dairaId) => {
    let dairaLabel = await getApiDairaName([dairaId])
    if (dairaLabel.data.length > 0) return dairaLabel.data[0].name_ar
    else return 'N/A'
  }

  const providerValue = {
    init,
    isLoading,
    challengeParticipationStat,
    challengeParticipationFilter,
    setChallengeParticipationFilter,
    getChallengeParticipationStat,
    date,
    setDate,
    setlangDir,
    getDairaStat,
    statuses: statusesDictionnary
  }

  useEffect(() => {
    getChallengeParticipationStat()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeParticipationFilter, date, langDir])

  return (
    <ChallengeParticipationsDashboardContext.Provider value={providerValue}>
      {children}
    </ChallengeParticipationsDashboardContext.Provider>
  )
}
ChallengeParticipationsDashboardProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useChallengeParticipationsDashboard = () => {
  const context = useContext(ChallengeParticipationsDashboardContext)
  if (context === undefined) {
    throw new Error('useChallengeParticipationsDashboard must be used within a ChallengeParticipationsDashboardProvider')
  }
  return context
}
